<template lang="html">
  <sc-flex :is-column="true">
    <SigningCompletedDialog
      :opened.sync="signingCompletedDialogOpened"
      :taxReturn="taxReturn"
      :hasNextStep="hasNextStep"
      @go-to-next-step="done"
    />

    <SignaturePadDialog
      :opened.sync="signaturePadOpened"
      :signature-type.sync="signatureType"
      :image-signature.sync="imageSignature"
      :image-raw-data.sync="imageRawData"
      :text-signature.sync="textSignature"
      :saving="savingSignature"
      @saveSignature="saveSignature"
    />

    <!-- Partial: Alert -->
    <TaxReturnAlert>
      <sc-alert :type="signingRequired ? 'info' : 'success'">
        <span v-if="signingRequired">
          Please click the <b>Sign Here</b> button below to sign the pages.
        </span>

        <span v-if="currentUser.is_advisor">
          You are not required to sign this document.
        </span>

        <span v-else-if="eSignedCompleted">
          You've signed all the pages.
          {{
            k1DistributionEnabled
              ? sendK1PackagesAfterSignaturesCompleted
                ? 'If applicable, K-1 packages will be sent to the partners or members once all signatures are completed.'
                : 'The firm will send the K-1 packages to the partners or members.'
              : ''
          }}
        </span>

        <template v-else>
          <span>
            You've signed this page. Please click
            <b>Next</b> to continue.
          </span>
          <span class="px-1">
            {{ currentFormIndex + 1 }} / {{ eSignForms.length }}
          </span>
        </template>

        <sc-button-group slot="button-group">
          <sc-button
            type="ghost"
            :is-disabled="currentFormIndex === 0"
            :is-icon-only="true"
            icon-left="arrow-left"
            @click="back()"
          >
          </sc-button>
          <sc-button
            v-if="hasNext"
            type="ghost"
            @click="next()"
            :is-disabled="signingRequired"
            icon-right="arrow-right"
          >
            Next
          </sc-button>
          <template v-if="eSignedCompleted || taxReturnFinished">
            <sc-button
              v-if="hasNextStep"
              :type="taxReturnFinished ? 'ghost' : 'primary'"
              :icon-right="taxReturnFinished ? 'arrow-right' : 'check'"
              @click="done()"
              data-test-id="done-signing-button"
            >
              {{ taxReturnFinished ? 'Next' : 'Done' }}
            </sc-button>

            <sc-button
              v-else
              type="primary"
              class="hidden-xs-only"
              @click="backToDashboard()"
              data-test-id="back-to-dashboard-after-signing-button"
            >
              Back to Dashboard
            </sc-button>
          </template>
        </sc-button-group>
      </sc-alert>
    </TaxReturnAlert>

    <div class="main-pdf-container">
      <TaxReturnLoading v-if="pdfLoading" />

      <!-- Partial: Container -->
      <div
        class="pdf-page-container"
        ref="pdfContainer"
        :style="{ width: pdfPageWidth + 'px', height: pdfPageHeight + 'px' }"
      >
        <!-- Partial: PDF -->
        <div id="pdf-annotation" class="pdf" />

        <!-- Partial: Signatures -->
        <div
          v-for="(signature, index) in currentForm.signatures"
          v-if="pdfLoaded"
          :key="'signature' + index"
          class="signature-container"
          :data-signatureid="index"
          :style="
            getStylePosition({
              pageWidth: signature.pageWidth,
              elementBottom: signature.signatureBottom,
              elementLeft: signature.signatureLeft,
            })
          "
        >
          <template v-if="shouldShowSignature(signature)">
            <img
              v-if="signature.signatureType === 'image'"
              :style="
                getStyleDimension({
                  pageWidth: signature.pageWidth,
                  elementWidth: signature.signatureWidth,
                  elementHeight: signature.signatureHeight,
                })
              "
              :src="signature.signatureData"
            />
            <v-layout
              v-else-if="signature.signatureType === 'text'"
              align-center
              justify-center
              :style="
                getStyleDimension({
                  pageWidth: signature.pageWidth,
                  elementWidth: signature.signatureWidth,
                  elementHeight: signature.signatureHeight,
                })
              "
            >
              <span
                class="signature-text"
                :style="
                  getStyleSignatureText({
                    pageWidth: signature.pageWidth,
                    fontSize: signature.signatureTextSize,
                  })
                "
              >
                {{ signature.signatureData }}
              </span>
            </v-layout>
            <v-layout
              v-else
              class="sign-area"
              :style="
                getStyleDimension({
                  pageWidth: signature.pageWidth,
                  elementWidth: signature.signatureWidth,
                  elementHeight: signature.signatureHeight,
                })
              "
              @click="
                taxReturnFinished
                  ? showFinishedWarning()
                  : openSignaturePad(index)
              "
            >
              <div class="signer-title">
                <span v-if="signature.name">{{ signature.name }}</span>
              </div>
              <h2>Sign here</h2>
            </v-layout>
          </template>
        </div>

        <!-- Partial: Signature Dates -->
        <div
          v-for="(signature, index) in currentForm.signatures"
          v-if="
            pdfLoaded && signature.signatureDate && !signature.hideSignatureDate
          "
          class="signature-date-container"
          :key="'date' + index"
          :data-signatureid="index"
          :style="
            getStylePosition({
              pageWidth: signature.pageWidth,
              elementBottom: signature.signatureDateBottom,
              elementLeft: signature.signatureDateLeft,
            })
          "
        >
          <div
            v-if="shouldShowSignature(signature)"
            class="signature-date-container-block"
            :style="
              getStyleDimension({
                pageWidth: signature.pageWidth,
                elementWidth: signature.signatureDateWidth,
                elementHeight: signature.signatureDateHeight,
              })
            "
          >
            <span
              class="signature-date"
              :style="
                getStyleSignatureDateText({
                  pageWidth: signature.pageWidth,
                  fontSize: signature.signatureTextSize,
                })
              "
            >
              {{ signature.signatureDate }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </sc-flex>
</template>

<script>
import { mapGetters } from 'vuex';
import { PDFDocument } from 'pdf-lib';
import pdf from 'vue-pdf';
import {
  PDFJS_CMAP_URL,
  PDFJS_STANDARD_FONT_DATA_URL,
  PDFJS_WORKER_SRC,
} from '@/utils/pdf';
import SignaturePadDialog from '@/components/dialogs/SignaturePadDialog';
import axios from 'axios';
import moment from 'moment';
import download from 'downloadjs';
import has from 'lodash/has';
import get from 'lodash/get';
import SignedDocumentList from '@/components/returns/SignedDocumentList';
import API from '@/api';
import SigningCompletedDialog from './components/SigningCompletedDialog';
import SignStepMixin from '@/mixins/SignStepMixin';
import TaxReturnAlert from './components/TaxReturnAlert';
import TaxReturnLoading from './components/TaxReturnLoading';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  components: {
    pdf,
    SignaturePadDialog,
    SignedDocumentList,
    SigningCompletedDialog,
    TaxReturnAlert,
    TaxReturnLoading,
  },

  mixins: [SignStepMixin, FirmPreferenceMixins],

  props: {
    companyId: {
      type: String,
      required: true,
    },

    currentCompany: {
      type: Object,
      default: () => {},
    },

    currentIdentity: {
      type: Object,
      default: () => {},
    },

    sessionId: {
      type: String,
      default: '',
    },

    taxReturn: {
      type: Object,
      required: true,
    },

    hasNextStep: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      MAX_PDF_WIDTH: 918,

      currentFormIndex: 0,

      pdfMutationObserver: null,
      pdfPageWidth: 0,
      pdfPageHeight: 0,

      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfProgress: 0,
      pdfLoaded: false,
      pdfPageIndex: 1,

      currentSignatureIndex: null,
      signaturePadOpened: false,

      signatureType: 'image',
      imageSignature: '',
      imageRawData: null,
      textSignature: '',
      savedSignaturesByName: {},
      savingSignature: false,

      uploading: false,
      signingCompletedDialogOpened: false,
    };
  },

  methods: {
    getCanvasDimensions() {
      const pdfContainer = this.$refs.pdfContainer;
      if (!pdfContainer) return { height: 0, width: 0 };

      const { width } = pdfContainer.getBoundingClientRect();

      if (this.pdfLoaded && this.pdfDoc) {
        this.renderPdfWithAnnotations();
      }
    },

    getPageRatio(pageWidth) {
      if (this.pdfPageWidth === null) {
        return 1;
      }

      const ratio = this.pdfPageWidth / pageWidth;
      const maxRatio = 1.5;

      return Math.min(ratio, maxRatio);
    },

    getStylePosition({ pageWidth, elementBottom, elementLeft }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        bottom: elementBottom * ratio + 'px',
        left: elementLeft * ratio + 'px',
      };
    },

    getScale(page) {
      // This scale makes PDF look bigger
      let initialScale = 1.5;

      const initialViewport = page.getViewport({ scale: initialScale });

      const maxWidth = this.MAX_PDF_WIDTH;

      let newScale = initialScale;

      // If the initialScale makes the PDF too big, we reduce it so that it fits within max width
      if (initialViewport.width > maxWidth) {
        newScale = (maxWidth / initialViewport.width) * initialScale;
      }

      return newScale;
    },

    async updatePdfPageDimensions() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.pdfPageIndex);
      const scale = this.getScale(page);
      const viewport = page.getViewport({ scale });

      this.pdfPageWidth = viewport.width;
      this.pdfPageHeight = viewport.height;
    },

    getStyleDimension({ pageWidth, elementWidth, elementHeight }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        width: elementWidth * ratio + 'px',
        height: elementHeight * ratio + 'px',
      };
    },

    getStyleSignatureText({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: fontSize * ratio + 'px',
      };
    },

    getStyleSignatureDateText({ pageWidth, fontSize }) {
      const ratio = this.getPageRatio(pageWidth);

      return {
        fontSize: fontSize * ratio + 'px',
      };
    },

    openSignaturePad(signatureIndex) {
      this.signaturePadOpened = true;
      this.currentSignatureIndex = signatureIndex;

      const signature = this.currentForm.signatures[signatureIndex];
      const savedSignature = this.savedSignaturesByName[signature.name];

      this.signatureType = savedSignature
        ? savedSignature.signatureType
        : 'image';
      this.imageSignature =
        savedSignature && savedSignature.signatureType === 'image'
          ? savedSignature.signatureData
          : '';
      this.textSignature =
        savedSignature && savedSignature.signatureType === 'text'
          ? savedSignature.signatureData
          : '';
    },

    showFinishedWarning() {
      this.$store.dispatch('application/setErrorNotification', {
        message:
          'You cannot sign a finished tax return. Please reach out to your advisor to re-open the tax return.',
      });
    },

    async saveSignature() {
      try {
        this.savingSignature = true;

        // prettier-ignore
        const signature = this.currentForm.signatures[this.currentSignatureIndex];

        // For some reason, some browsers do not return the correct image data
        // This is rare and we need to prevent storing invalid image data
        if (
          this.signatureType === 'image' &&
          !this.imageSignature &&
          !this.imageSignature.startsWith('data:image/png;base64,')
        ) {
          this.$store.dispatch('application/setErrorNotification', {
            message: "We couldn't capture your signature. Please try again.",
          });
          return;
        }

        this.savedSignaturesByName[signature.name] = {
          signatureType: this.signatureType,
          signatureData:
            this.signatureType === 'image'
              ? this.imageSignature
              : this.textSignature,
        };

        const response = await API.taxReturn.saveSignature({
          id: this.taxReturn.id,
          companyId: this.companyId,
          formIndex: this.currentFormIndex,
          signatureIndex: this.currentSignatureIndex,
          signatureType: this.signatureType,
          signatureData:
            this.signatureType === 'image'
              ? this.imageSignature
              : this.textSignature,
          signatureRawData:
            this.signatureType === 'image' ? this.imageRawData : null,
          signatureDate: moment().format('L'),
          sessionId: this.sessionId,
        });

        this.$emit('tax-return-updated', response.data);
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem signing the document.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.savingSignature = false;
        this.signaturePadOpened = false;
      }
    },

    back() {
      if (this.currentFormIndex > 0) {
        this.currentFormIndex = this.currentFormIndex - 1;
        this.pdfPageIndex = this.eSignForms[this.currentFormIndex].page;
        this.renderPdfWithAnnotations();
      }
    },

    next() {
      if (this.hasNext && !this.signingRequired) {
        this.currentFormIndex = this.currentFormIndex + 1;
        this.pdfPageIndex = this.eSignForms[this.currentFormIndex].page;
        this.renderPdfWithAnnotations();
      }
    },

    done() {
      this.$emit('finished-signing');
    },

    backToDashboard() {
      if (this.isEmbeddedInNext) {
        window.location.href = '/';
      } else {
        this.$router.push({ name: 'dashboard' });
      }
    },

    async downloadForm() {
      const response = await API.integration.downloadDocument({
        companyId: this.companyId,
        link: this.taxReturn.watermarked_file,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(pdfBytes);

      const formPdf = await PDFDocument.create();
      const [page] = await formPdf.copyPages(pdfDoc, [
        this.currentForm.page - 1,
      ]);
      formPdf.addPage(page);

      const formBytes = await formPdf.save();
      download(
        formBytes,
        this.currentForm.form_name + ' ' + this.currentForm.form_id,
        'application/pdf',
      );
    },

    async uploadSignedForm(e) {
      this.uploading = true;
      try {
        const file = e.target.files[0];

        const {
          data: { id, upload_link },
        } = await API.integration.getTemporaryUploadLink({
          companyId: this.companyId,
        });

        await API.integration.uploadToDropbox(upload_link, file);

        const path = 'Client Manually Signed Forms';

        const response = await API.integration.processDocument({
          id,
          documentName: file.name,
          companyId: this.companyId,
          path: '/'.concat(path),
        });

        // Remove firm name & company name from path
        const list = response.data.document_location.split('/');
        list.splice(0, 3);

        const documentPath = list.join('/');

        await this.saveSignedForm(documentPath);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: has(error, 'message')
            ? get(error, 'mesage')
            : 'There was a problem uploading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.uploading = false;
      }
    },

    async saveSignedForm(documentPath) {
      if (!this.currentForm.wetSignatures) {
        this.currentForm.wetSignatures = [];
      }

      this.currentForm.wetSignatures.push({
        signedDocumentPath: documentPath,
      });

      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.companyId,
        e_sign_forms: this.taxReturn.e_sign_forms,
      });

      this.$emit('tax-return-updated', response.data);

      this.$store.dispatch('application/setSuccessNotification', {
        message: `You have successfully uploaded the document!`,
      });
    },

    async renderPdfWithAnnotations() {
      if (!this.pdfDoc) return;

      const pageNumber = this.pdfPageIndex || 1;
      const page = await this.pdfDoc.getPage(pageNumber);

      const scale = this.getScale(page);

      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      canvas.style.width = '100%';
      canvas.style.height = 'auto';

      const renderTask = page.render({
        canvasContext: context,
        viewport: viewport,
      });

      await renderTask.promise;

      const container = document.getElementById('pdf-annotation');
      container.innerHTML = '';
      container.appendChild(canvas);

      this.pdfLoaded = true;

      this.updatePdfPageDimensions();
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    eSignForms() {
      return this.taxReturn.e_sign_forms.filter(form => {
        return !form.wetSignaturesRequired;
      });
    },

    taxReturnFinished() {
      return this.taxReturn.status === 'finished';
    },

    currentForm() {
      return this.eSignForms[this.currentFormIndex];
    },

    signingRequired() {
      const eSignedCompleted = this.currentForm.signatures
        .filter(signature => this.shouldShowSignature(signature))
        .every(signature => signature.signatureData);

      return !eSignedCompleted;
    },

    eSignedCompleted() {
      return this.eSignForms.every(form => {
        return form.signatures
          .filter(signature => this.shouldShowSignature(signature))
          .every(signature => signature.signatureData);
      });
    },

    hasNext() {
      return this.currentFormIndex < this.eSignForms.length - 1;
    },

    k1DistributionEnabled() {
      return (
        this.taxReturnK1DistributionEnabled &&
        this.taxReturn.k1_distribution_status !== 'not_applicable'
      );
    },
  },

  created() {
    window.addEventListener('resize', this.getCanvasDimensions);
  },

  destroyed() {
    window.removeEventListener('resize', this.getCanvasDimensions);
  },

  beforeDestroy() {
    if (this.pdfMutationObserver) {
      this.pdfMutationObserver.disconnect();
    }
  },

  watch: {
    currentFormIndex() {
      if (this.currentForm) {
        this.pdfPageIndex = this.currentForm.page;
      }
    },

    allSigned() {
      if (this.allSigned) {
        this.signingCompletedDialogOpened = true;
      }
    },

    async pdfLoaded(value) {
      if (value) {
        this.getCanvasDimensions();
      }
    },
  },

  async mounted() {
    window.addEventListener('resize', this.updatePdfPageDimensions);

    this.$nextTick(() => {
      this.updatePdfPageDimensions();
    });

    try {
      this.pdfLoading = true;
      // This ensures that the pdf is rendered before we retrieve the canvas dimensions

      // Step 1: Fetch the document from API
      const response = await API.integration.downloadDocument({
        companyId: this.companyId,
        link: this.taxReturn.watermarked_file,
      });

      if (!response.data.document) {
        console.error('No document data found.');
        //display error message
        this.$store.dispatch('application/setErrorNotification', {
          message: 'No document data found.',
        });
      }

      const pdfSrc = response.data.document.data.link;
      const pdflib = window.pdfjsLib;
      pdflib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;

      const loadingTask = pdflib.getDocument({
        url: pdfSrc,
        cMapUrl: PDFJS_CMAP_URL,
        cMapPacked: true,
        standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
      });
      this.pdfDoc = await loadingTask.promise;

      // Step 3: Set the page index
      this.pdfPageIndex = this.eSignForms[this.currentFormIndex].page;
      await this.renderPdfWithAnnotations();
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="css" scoped>
.main-pdf-container {
  position: relative;

  overflow: auto;
}

.pdf {
  position: relative;
  padding: 0;
  width: 100%;
  display: flex;
}

.pdf-page-container {
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
}

.button-containers {
  flex-grow: 0;
}

.signature-container {
  position: relative;
  display: flex;
}

.signature-date-container,
.signature-date-container-block {
  align-items: center;
  display: flex;
  justify-content: center;
}

.signature-container,
.signature-date-container {
  position: absolute;
}

.sign-area {
  display: flex;
  flex-direction: column;
  height: 100%;

  background-color: rgba(76, 175, 80, 0.85);
  border: 2px dashed rgba(0, 0, 0, 0.75);
  border-radius: 4px;
  color: #fff;
  backdrop-filter: blur(0.5px);

  font-size: 1em;
  font-weight: bold;
  text-align: left;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
  user-select: none;
  cursor: pointer;
}

.sign-area .signer-title {
  position: absolute;
  top: -32px;
  left: -2px;

  padding: 2px 16px;

  background-color: rgba(40, 50, 80, 0.9);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.5);
  color: rgba(255, 255, 255, 0.75);

  font-size: 14px !important;
  font-weight: medium;
  line-height: 24px !important;
}

.sign-area h2 {
  display: flex;
  flex: auto;
  align-items: center;
  padding: 0px 16px;
}

.sign-area .signer-title {
  padding: 0px 16px;
  flex: auto;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.signature-text {
  font-family: 'Zeyada', cursive;
  /* To make sure the signature text appear in one line */
  white-space: nowrap;
}
.signature-date {
  font-family: 'Mali', cursive;
  /* To make sure the signature date appear in one line */
  white-space: nowrap;
}

.download-and-sign-container {
  width: 720px;
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  padding: 30px 30px 20px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.steps {
  list-style-type: decimal;
  font-size: 16px;
}

.steps li {
  margin-top: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.steps li:last-of-type {
  border-bottom: none;
}
</style>
